/**
 * GameWindowManager - Coordinates multiple GameWindow instances.
 * Simplified from windowlite's WindowManager — registry, z-index, shortcuts.
 */

import GameWindow from './GameWindow.js';

export default class GameWindowManager {
  constructor(options) {
    if (!options.container) throw new Error('GameWindowManager: container is required');

    this.container = options.container;
    this.windows = new Map();
    this.shortcuts = new Map();
    this._shortcutsEnabled = true;

    // Ensure container has positioning context
    const pos = getComputedStyle(this.container).position;
    if (pos === 'static') this.container.style.position = 'relative';

    this.container.classList.add('gw-container');

    this._setupKeyboardHandler();
    this._setupResizeObserver();
    this._setupBlurOnOutsideClick();
  }

  // ---- Window Lifecycle ----

  create(options) {
    if (this.windows.has(options.id)) {
      console.warn(`GameWindowManager: window '${options.id}' already exists`);
      return this.windows.get(options.id);
    }

    const win = new GameWindow({
      ...options,
      container: this.container,
    });

    this.windows.set(options.id, win);
    return win;
  }

  get(id) {
    return this.windows.get(id);
  }

  toggle(id) {
    const win = this.windows.get(id);
    if (win) win.toggle();
  }

  show(id) {
    const win = this.windows.get(id);
    if (win) win.show();
  }

  hide(id) {
    const win = this.windows.get(id);
    if (win) win.hide();
  }

  destroy(id) {
    const win = this.windows.get(id);
    if (win) {
      win.destroy();
      this.windows.delete(id);
    }
  }

  destroyAll() {
    for (const [id, win] of this.windows) {
      win.destroy();
    }
    this.windows.clear();
  }

  // ---- Keyboard Shortcuts ----

  registerShortcut(windowId, key) {
    this.shortcuts.set(key.toLowerCase(), windowId);
  }

  enableShortcuts() { this._shortcutsEnabled = true; }
  disableShortcuts() { this._shortcutsEnabled = false; }

  _setupKeyboardHandler() {
    this._onKeyDown = (e) => {
      if (!this._shortcutsEnabled) return;

      // Skip when typing in form elements
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (document.activeElement?.isContentEditable) return;

      const windowId = this.shortcuts.get(e.key.toLowerCase());
      if (windowId) {
        e.preventDefault();
        this.toggle(windowId);
      }
    };

    document.addEventListener('keydown', this._onKeyDown);
  }

  // ---- Blur on outside click ----

  _setupBlurOnOutsideClick() {
    this._onContainerPointerDown = (e) => {
      // If click target is inside a window or sidebar, let focus() handle it
      if (e.target.closest('.gw-window, .gw-sidebar')) return;
      GameWindow.blurAll(this.container);
    };
    this.container.addEventListener('pointerdown', this._onContainerPointerDown, { passive: true });
  }

  // ---- Container Resize ----

  _setupResizeObserver() {
    this._resizeObserver = new ResizeObserver(() => {
      for (const [, win] of this.windows) {
        if (win.isVisible()) win.ensureFitInContainer();
      }
    });
    this._resizeObserver.observe(this.container);
  }

  // ---- Cleanup ----

  dispose() {
    document.removeEventListener('keydown', this._onKeyDown);
    if (this._onContainerPointerDown) {
      this.container.removeEventListener('pointerdown', this._onContainerPointerDown);
    }
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
    this.destroyAll();
  }
}
